import { getSupabase } from "../components/supabaseClient";

/**
 * Comprehensive Corti transcription logging utility
 * Logs every transcription with extensive details for tracking and control
 */

export class CortiLogger {
  constructor() {
    this.sessionId = this.generateSessionId();
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Log a transcription event to the corti_logs table
   */
  async logTranscription({
    patientId,
    patientName,
    transcriptionType, // 'live' or 'recorded'
    audioFileName,
    audioDurationSeconds,
    audioSource, // 'url', 'file', 'live_stream'
    audioUrl,
    creditUsage,
    transcriptLength,
    factsExtracted,
    soapNotesGenerated,
    processingTimeSeconds,
    status = 'completed',
    errorMessage = null,
    metadata = {}
  }) {
    try {
      console.log("📊 CortiLogger: Starting logTranscription...");
      console.log("📊 CortiLogger: Input parameters:", {
        patientId,
        patientName,
        transcriptionType,
        audioFileName,
        audioDurationSeconds,
        audioSource,
        audioUrl,
        creditUsage,
        transcriptLength,
        factsExtracted,
        soapNotesGenerated,
        processingTimeSeconds,
        status,
        errorMessage,
        metadata
      });

      const supabase = await getSupabase();
      console.log("📊 CortiLogger: Supabase client obtained");
      
      const { data: { session } } = await supabase.auth.getSession();
      console.log("📊 CortiLogger: Session data:", session);
      
      if (!session?.user) {
        console.error("📊 CortiLogger: No authenticated user found");
        console.error("📊 CortiLogger: Session:", session);
        return null;
      }

      console.log("📊 CortiLogger: User authenticated:", {
        id: session.user.id,
        email: session.user.email
      });

      const logData = {
        user_id: session.user.id,
        user_email: session.user.email,
        patient_id: patientId,
        patient_name: patientName,
        transcription_type: transcriptionType,
        audio_file_name: audioFileName,
        audio_duration_seconds: audioDurationSeconds,
        audio_source: audioSource,
        audio_url: audioUrl,
        credit_usage: creditUsage,
        transcript_length: transcriptLength,
        facts_extracted: factsExtracted,
        soap_notes_generated: soapNotesGenerated,
        processing_time_seconds: processingTimeSeconds,
        status: status,
        error_message: errorMessage,
        session_id: this.sessionId,
        metadata: metadata
      };

      console.log("📊 CortiLogger: Prepared log data:", logData);

      console.log("📊 CortiLogger: Inserting into corti_logs table...");
      const { data, error } = await supabase
        .from('corti_logs')
        .insert([logData])
        .select()
        .single();

      console.log("📊 CortiLogger: Insert completed. Data:", data, "Error:", error);

      if (error) {
        console.error("📊 CortiLogger: Error logging transcription:", error);
        console.error("📊 CortiLogger: Error details:", {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        return null;
      }

      console.log("📊 CortiLogger: Successfully logged transcription:", data);
      return data;
    } catch (error) {
      console.error("📊 CortiLogger: Exception logging transcription:", error);
      console.error("📊 CortiLogger: Error stack:", error.stack);
      return null;
    }
  }

  /**
   * Update user credit usage in the users table
   */
  async updateUserCredits(creditUsage) {
    try {
      console.log("📊 CortiLogger: Starting updateUserCredits...");
      console.log("📊 CortiLogger: Credit usage to add:", creditUsage);
      
      const supabase = await getSupabase();
      console.log("📊 CortiLogger: Supabase client obtained for credit update");
      
      const { data: { session } } = await supabase.auth.getSession();
      console.log("📊 CortiLogger: Session data for credit update:", session);
      
      if (!session?.user) {
        console.error("📊 CortiLogger: No authenticated user found for credit update");
        console.error("📊 CortiLogger: Session:", session);
        return false;
      }

      console.log("📊 CortiLogger: Updating user credits:", {
        userId: session.user.id,
        userEmail: session.user.email,
        creditUsage: creditUsage
      });

      // Get current user data
      console.log("📊 CortiLogger: Fetching current user data...");
      const { data: userData, error: fetchError } = await supabase
        .from('users')
        .select('credits_used_this_month, credits_used_all_time, credit_limit, last_credit_reset_date')
        .eq('id', session.user.id)
        .single();

      console.log("📊 CortiLogger: User data fetch completed. Data:", userData, "Error:", fetchError);

      if (fetchError) {
        console.error("📊 CortiLogger: Error fetching user data:", fetchError);
        console.error("📊 CortiLogger: Error details:", {
          code: fetchError.code,
          message: fetchError.message,
          details: fetchError.details,
          hint: fetchError.hint
        });
        return false;
      }

      // Check if monthly reset is needed before calculating new totals
      const lastResetDate = userData.last_credit_reset_date ? new Date(userData.last_credit_reset_date) : null;
      const now = new Date();
      const needsMonthlyReset = lastResetDate && (
        lastResetDate.getMonth() !== now.getMonth() || 
        lastResetDate.getFullYear() !== now.getFullYear()
      );

      // If monthly reset is needed, we should call the reset function first
      // But for now, we'll just update both columns
      // The reset function will handle proper monthly resets
      
      // Calculate new credit totals
      const currentMonthly = userData.credits_used_this_month || 0;
      const currentAllTime = userData.credits_used_all_time || 0;
      const newMonthlyCredits = currentMonthly + creditUsage;
      const newAllTimeCredits = currentAllTime + creditUsage;

      // Check if user would exceed their monthly limit
      const creditLimit = userData.credit_limit || 15.0;
      const wouldExceedMonthly = newMonthlyCredits > creditLimit;

      console.log("📊 CortiLogger: Credit usage analysis:", {
        currentMonthly: currentMonthly,
        currentAllTime: currentAllTime,
        newMonthly: newMonthlyCredits,
        newAllTime: newAllTimeCredits,
        limit: creditLimit,
        wouldExceedMonthly,
        needsMonthlyReset
      });

      // Update user credits (both monthly and all-time)
      console.log("📊 CortiLogger: Updating user credits in database...");
      const { error: updateError } = await supabase
        .from('users')
        .update({
          credits_used_this_month: newMonthlyCredits,
          credits_used_all_time: newAllTimeCredits
        })
        .eq('id', session.user.id);

      console.log("📊 CortiLogger: Credit update query completed. Error:", updateError);

      if (updateError) {
        console.error("📊 CortiLogger: Error updating user credits:", updateError);
        console.error("📊 CortiLogger: Error details:", {
          code: updateError.code,
          message: updateError.message,
          details: updateError.details,
          hint: updateError.hint
        });
        return false;
      }

      console.log("📊 CortiLogger: Successfully updated user credits");
      
      // Return whether monthly limit would be exceeded
      return {
        success: true,
        wouldExceedMonthly,
        newMonthlyCredits,
        creditLimit
      };
    } catch (error) {
      console.error("📊 CortiLogger: Exception updating user credits:", error);
      console.error("📊 CortiLogger: Error stack:", error.stack);
      return false;
    }
  }

  /**
   * Get user's current credit usage and limits
   */
  async getUserCreditStatus() {
    try {
      console.log("📊 CortiLogger: Starting getUserCreditStatus...");
      
      const supabase = await getSupabase();
      console.log("📊 CortiLogger: Supabase client obtained for credit status");
      
      const { data: { session } } = await supabase.auth.getSession();
      console.log("📊 CortiLogger: Session data for credit status:", session);
      
      if (!session?.user) {
        console.error("📊 CortiLogger: No authenticated user found for credit status");
        console.error("📊 CortiLogger: Session:", session);
        return null;
      }

      console.log("📊 CortiLogger: Fetching credit status for user:", {
        id: session.user.id,
        email: session.user.email
      });

      const { data, error } = await supabase
        .from('users')
        .select('credits_used_this_month, credit_limit')
        .eq('id', session.user.id)
        .single();

      console.log("📊 CortiLogger: Credit status query completed. Data:", data, "Error:", error);

      if (error) {
        console.error("📊 CortiLogger: Error fetching user credit status:", error);
        console.error("📊 CortiLogger: Error details:", {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        return null;
      }

      const creditLimit = data.credit_limit || 15.0;
      const monthlyRemaining = Math.max(0, creditLimit - (data.credits_used_this_month || 0));

      console.log("📊 CortiLogger: Credit status calculated:", {
        creditLimit,
        monthlyUsed: data.credits_used_this_month || 0,
        monthlyRemaining,
        monthlyExceeded: (data.credits_used_this_month || 0) > creditLimit
      });

      return {
        creditLimit,
        monthlyUsed: data.credits_used_this_month || 0,
        monthlyRemaining,
        monthlyExceeded: (data.credits_used_this_month || 0) > creditLimit
      };
    } catch (error) {
      console.error("📊 CortiLogger: Exception getting user credit status:", error);
      console.error("📊 CortiLogger: Error stack:", error.stack);
      return null;
    }
  }

  /**
   * Get transcription logs for the current user
   */
  async getUserTranscriptionLogs(limit = 50) {
    try {
      console.log("📊 CortiLogger: Starting getUserTranscriptionLogs...");
      console.log("📊 CortiLogger: Limit:", limit);
      
      const supabase = await getSupabase();
      console.log("📊 CortiLogger: Supabase client obtained for transcription logs");
      
      const { data: { session } } = await supabase.auth.getSession();
      console.log("📊 CortiLogger: Session data for transcription logs:", session);
      
      if (!session?.user) {
        console.error("📊 CortiLogger: No authenticated user found for transcription logs");
        console.error("📊 CortiLogger: Session:", session);
        return [];
      }

      console.log("📊 CortiLogger: Fetching transcription logs for user:", {
        id: session.user.id,
        email: session.user.email,
        limit: limit
      });

      const { data, error } = await supabase
        .from('corti_logs')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      console.log("📊 CortiLogger: Transcription logs query completed. Data:", data, "Error:", error);

      if (error) {
        console.error("📊 CortiLogger: Error fetching user transcription logs:", error);
        console.error("📊 CortiLogger: Error details:", {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        return [];
      }

      console.log("📊 CortiLogger: Returning", data?.length || 0, "transcription logs");
      return data || [];
    } catch (error) {
      console.error("📊 CortiLogger: Exception getting user transcription logs:", error);
      console.error("📊 CortiLogger: Error stack:", error.stack);
      return [];
    }
  }

  /**
   * Get all transcription logs (admin only)
   */
  async getAllTranscriptionLogs(limit = 100) {
    try {
      console.log("📊 CortiLogger: Starting getAllTranscriptionLogs...");
      console.log("📊 CortiLogger: Limit:", limit);
      
      const supabase = await getSupabase();
      console.log("📊 CortiLogger: Supabase client obtained for all transcription logs");
      
      const { data: { session } } = await supabase.auth.getSession();
      console.log("📊 CortiLogger: Session data for all transcription logs:", session);
      
      if (!session?.user) {
        console.error("📊 CortiLogger: No authenticated user found for all transcription logs");
        console.error("📊 CortiLogger: Session:", session);
        return [];
      }

      console.log("📊 CortiLogger: Checking admin status for user:", {
        id: session.user.id,
        email: session.user.email
      });

      // Check if user is admin
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('role')
        .eq('id', session.user.id)
        .single();

      console.log("📊 CortiLogger: Admin check completed. Data:", userData, "Error:", userError);

      if (userError || userData?.role !== 'admin') {
        console.error("📊 CortiLogger: User is not admin. Role:", userData?.role);
        console.error("📊 CortiLogger: User error:", userError);
        return [];
      }

      console.log("📊 CortiLogger: User is admin, fetching all transcription logs...");

      const { data, error } = await supabase
        .from('corti_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      console.log("📊 CortiLogger: All transcription logs query completed. Data:", data, "Error:", error);

      if (error) {
        console.error("📊 CortiLogger: Error fetching all transcription logs:", error);
        console.error("📊 CortiLogger: Error details:", {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        return [];
      }

      console.log("📊 CortiLogger: Returning", data?.length || 0, "all transcription logs");
      return data || [];
    } catch (error) {
      console.error("📊 CortiLogger: Exception getting all transcription logs:", error);
      console.error("📊 CortiLogger: Error stack:", error.stack);
      return [];
    }
  }
}

// Export a singleton instance
export const cortiLogger = new CortiLogger();
